import React, { useEffect, useMemo, useState } from 'react';
import { Button, Inspector, List, ListItem, Modal, Thumbnail, token, Tooltip } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconDownload } from '@bynder/icons';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import useAvailableExportFormats from 'packages/hooks/useAvailableExportFormats';
import { setSaveStatus } from 'packages/store/creativeEditor/creativeEditor.actions';
import useEditor from 'packages/hooks/useEditor';
import generateTestId from 'packages/helpers/testIdHelpers';
import modalContainer from 'packages/common/modalContainer';

export const ExportFormats = () => {
    const dispatch = useThunkDispatch();
    const { translate } = useTranslate();
    const { creativeType, exportConfigurationIds, setExportConfigurationIds } = useEditor();
    const { isLoadingExportFormats, availableExportFormats } = useAvailableExportFormats({
        creativeType,
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [draftIds, setDraftIds] = useState<number[]>(exportConfigurationIds);

    const selectedCount = exportConfigurationIds.length;
    const testId = generateTestId('global_export_formats');

    const availableFormatIds = useMemo(
        () => new Set(availableExportFormats.map(({ id }) => id)),
        [availableExportFormats],
    );
    const totalFormatsCount = availableExportFormats.length;
    const isEmptySelection = draftIds.length === 0;
    const areAllFormatsSelected = totalFormatsCount > 0 && draftIds.length === totalFormatsCount;
    const isPartiallySelected = draftIds.length > 0 && draftIds.length < totalFormatsCount;

    useEffect(() => {
        if (isLoadingExportFormats) {
            return;
        }

        const nextSelectedIds = exportConfigurationIds.filter((id) => availableFormatIds.has(id));

        if (nextSelectedIds.length !== exportConfigurationIds.length) {
            setExportConfigurationIds(nextSelectedIds);
            dispatch(setSaveStatus(false));
        }
    }, [availableFormatIds, dispatch, isLoadingExportFormats, exportConfigurationIds, setExportConfigurationIds]);

    const openModal = () => {
        // Empty array means "all formats" — show all as checked in the modal
        const initialDraft =
            exportConfigurationIds.length === 0 ? availableExportFormats.map(({ id }) => id) : exportConfigurationIds;
        setDraftIds(initialDraft);
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const hasChanges = useMemo(() => {
        const nextIds = draftIds.length === availableExportFormats.length ? [] : draftIds;

        if (nextIds.length !== exportConfigurationIds.length) {
            return true;
        }

        const sortedNext = [...nextIds].sort((a, b) => a - b);
        const sortedCurrent = [...exportConfigurationIds].sort((a, b) => a - b);

        return sortedNext.some((id, i) => id !== sortedCurrent[i]);
    }, [draftIds, exportConfigurationIds, availableExportFormats.length]);

    const isSaveDisabled = isEmptySelection || !hasChanges;

    const handleSave = () => {
        // Normalize back to [] when all formats are selected ("all formats" sentinel)
        const nextIds = draftIds.length === availableExportFormats.length ? [] : draftIds;
        setExportConfigurationIds(nextIds);
        dispatch(setSaveStatus(false));
        setIsModalOpen(false);
    };

    const toggleDraftFormat = (id: number) => {
        setDraftIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
    };

    const toggleSelectAll = () => {
        setDraftIds(areAllFormatsSelected ? [] : availableExportFormats.map(({ id }) => id));
    };

    const isDisabled = isLoadingExportFormats || !availableExportFormats.length;

    const subtextTrigger = isLoadingExportFormats
        ? translate('modal.quick-export.loading')
        : selectedCount
          ? translate('editor.export-formats.options-enabled', { count: selectedCount })
          : translate('editor.export-formats.all-formats-enabled');

    const selectedFormatNames = availableExportFormats
        .filter(({ id }) => exportConfigurationIds.includes(id))
        .map(({ displayName }) => displayName);

    const tooltipContent =
        selectedFormatNames.length > 0
            ? selectedFormatNames.map((name, i) => (
                  <React.Fragment key={name}>
                      {i > 0 && <br />}
                      {name}
                  </React.Fragment>
              ))
            : undefined;

    return (
        <>
            <Tooltip content={tooltipContent} isOpen={selectedFormatNames.length > 0 ? undefined : false}>
                {/* Clickable wrapper:
                    ListItem must not receive onClick (DS treats it as focusable),
                    so we attach the click handler to an outer div instead. */}
                <div
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={isDisabled ? undefined : openModal}
                    onKeyDown={
                        isDisabled
                            ? undefined
                            : (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') openModal();
                              }
                    }
                    style={{ cursor: isDisabled ? undefined : 'pointer' }}
                >
                    <ListItem
                        thumbnail={<Thumbnail backgroundColor={token.gray50a} icon={<IconDownload />} />}
                        subtext={subtextTrigger}
                        rightElements={
                            <Button variant="clean" isDisabled={isDisabled} onClick={openModal} {...testId}>
                                {translate('editor.export-formats.modal.edit')}
                            </Button>
                        }
                        isDisabled={isDisabled}
                    >
                        {translate('editor.export-formats.modal.title')}
                    </ListItem>
                </div>
            </Tooltip>
            <Inspector.Divider />
            <Modal
                container={modalContainer}
                title={translate('editor.export-formats.modal.title')}
                isOpen={isModalOpen}
                onClose={handleCancel}
                isClosedOnOverlayClick
                actionPrimary={
                    <Tooltip
                        content={
                            isEmptySelection
                                ? translate('editor.export-formats.modal.empty-selection-tooltip')
                                : undefined
                        }
                        isOpen={isEmptySelection ? undefined : false}
                    >
                        <Button variant="primary" isDisabled={isSaveDisabled} onClick={handleSave}>
                            {translate('editor.export-formats.modal.save')}
                        </Button>
                    </Tooltip>
                }
                actionSecondary={
                    <Button variant="secondary" onClick={handleCancel}>
                        {translate('editor.export-formats.modal.cancel')}
                    </Button>
                }
            >
                <p>{translate('editor.export-formats.modal.description')}</p>
                <List>
                    <List.Item
                        checkedVariant="checkbox"
                        isChecked={areAllFormatsSelected}
                        isIndeterminateCheckbox={isPartiallySelected}
                        onClick={toggleSelectAll}
                        subtext={translate('editor.export-formats.modal.selection-count', {
                            selected: draftIds.length,
                            total: totalFormatsCount,
                        })}
                    >
                        {translate('editor.export-formats.modal.select-all')}
                    </List.Item>
                    {availableExportFormats.map(({ id, displayName, description }) => (
                        <List.Item
                            key={id}
                            checkedVariant="checkbox"
                            isChecked={draftIds.includes(id)}
                            onClick={() => toggleDraftFormat(id)}
                            subtext={<span style={{ whiteSpace: 'normal', overflow: 'visible' }}>{description}</span>}
                        >
                            {displayName}
                        </List.Item>
                    ))}
                </List>
            </Modal>
        </>
    );
};
